/**
 * Cijfers Store — bedrijfscijfers-KPI's (/cijfers, admin).
 *
 * Leest de Brein-klare endpoints GET /api/kpi/overview (alle KPI's van één
 * periode + QoQ/YoY) en GET /api/kpi/series (tijdreeks per metric).
 * Series worden per (metric, grain) gecachet; de merge live-wint-van-Excel
 * gebeurt server-side, elk punt draagt zijn `source` mee zodat de grafiek
 * historische punten gedempt kan tonen.
 */

import { create } from 'zustand';
import { supabase } from '../lib/supabase';

// ─── Types ──────────────────────────────────────────────────────

export type Grain = 'month' | 'quarter' | 'year';

export type MetricFormat = 'currency' | 'currency2' | 'integer' | 'decimal1' | 'percent';

export interface OverviewMetric {
  key: string;
  label: string;
  unit: string;
  format: MetricFormat;
  lowerIsBetter: boolean;
  dimension: string;
  value: number | null;
  source: string;
  qoq_pct: number | null;
  yoy_pct: number | null;
}

export interface SeriesPoint {
  period: string; // YYYY-MM-DD (period_start)
  dimension: string;
  value: number | null;
  source: string; // 'live' | 'excel-historisch'
}

export interface Series {
  metric: string;
  label: string;
  unit: string;
  format: MetricFormat;
  lowerIsBetter: boolean;
  grain: Grain;
  points: SeriesPoint[];
}

export interface DailyOmzetDay {
  day: string; // YYYY-MM-DD
  verkoop: number;
  verkoopOrders: number;
  gefactureerd: number;
  gefactureerdFacturen: number;
  gepland: number;
  geplandOrders: number;
}

export interface DailyOmzet {
  from: string;
  to: string;
  today: string;
  days: DailyOmzetDay[];
}

export interface TargetInfo {
  metric: string;
  grain: Grain;
  period: string;
  /** null = geen doel ingesteld (ook niet via carry-forward). */
  target: number | null;
  /** period_start van de rij die het doel levert (carry-forward-bron). */
  definedAt: string | null;
}

// ─── Periode-helpers ────────────────────────────────────────────

/** Huidige periode voor een grain: '2026-Q3' | '2026-07' | '2026'. */
export function currentPeriod(grain: Grain): string {
  const now = new Date();
  const y = now.getFullYear();
  if (grain === 'month') return `${y}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  if (grain === 'year') return String(y);
  return `${y}-Q${Math.floor(now.getMonth() / 3) + 1}`;
}

export function shiftPeriod(period: string, grain: Grain, delta: number): string {
  if (grain === 'year') return String(Number(period) + delta);
  if (grain === 'quarter') {
    const [y, q] = period.split('-Q').map(Number);
    const idx = y * 4 + (q - 1) + delta;
    return `${Math.floor(idx / 4)}-Q${(idx % 4) + 1}`;
  }
  const [y, m] = period.split('-').map(Number);
  const idx = y * 12 + (m - 1) + delta;
  return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
}

const MONTH_NAMES = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

export function formatPeriodLabel(period: string, grain: Grain): string {
  if (grain === 'year') return period;
  if (grain === 'quarter') {
    const [y, q] = period.split('-Q');
    return `Q${q} ${y}`;
  }
  const [y, m] = period.split('-');
  return `${MONTH_NAMES[Number(m) - 1]} ${y}`;
}

/** Is deze period_start de nog lopende (onvolledige) periode? */
export function isRunningPeriod(periodStart: string, grain: Grain): boolean {
  const now = new Date();
  const [y, m] = periodStart.split('-').map(Number);
  if (grain === 'year') return y === now.getFullYear();
  if (grain === 'quarter') {
    return y === now.getFullYear() && Math.floor((m - 1) / 3) === Math.floor(now.getMonth() / 3);
  }
  return y === now.getFullYear() && m === now.getMonth() + 1;
}

/** period_start (YYYY-MM-DD) → korte as-label per grain. */
export function formatPointLabel(periodStart: string, grain: Grain): string {
  const [y, m] = periodStart.split('-');
  if (grain === 'year') return y;
  if (grain === 'quarter') return `Q${Math.floor((Number(m) - 1) / 3) + 1} '${y.slice(2)}`;
  return `${MONTH_NAMES[Number(m) - 1].slice(0, 3)} '${y.slice(2)}`;
}

/** Weergave-periode → niet-lopende vergelijkbaarheid is aan de lezer; API accepteert dit formaat direct. */

// ─── Waarde-formatting (NL) ─────────────────────────────────────

const nlInt = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 0 });
const nlDec1 = new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const nlEur0 = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const nlEur2 = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function formatMetricValue(value: number | null | undefined, format: MetricFormat): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  switch (format) {
    case 'currency':
      return nlEur0.format(value);
    case 'currency2':
      return nlEur2.format(value);
    case 'integer':
      return nlInt.format(value);
    case 'decimal1':
      return nlDec1.format(value);
    case 'percent':
      return `${nlDec1.format(value * 100)}%`;
  }
}

// ─── API ────────────────────────────────────────────────────────

async function apiFetch(url: string, init?: RequestInit): Promise<Response> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('Not authenticated');
  return fetch(url, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
}

// ─── Store ──────────────────────────────────────────────────────

interface CijfersState {
  grain: Grain;
  period: string;
  overview: Record<string, OverviewMetric[]> | null;
  overviewLoading: boolean;
  overviewError: string | null;
  seriesCache: Record<string, Series>;
  seriesLoading: Record<string, boolean>;
  dailyCache: Record<string, DailyOmzet>;
  targetCache: Record<string, TargetInfo>;

  setPeriod: (period: string, grain?: Grain) => void;
  setGrain: (grain: Grain) => void;
  loadOverview: () => Promise<void>;
  loadSeries: (metric: string) => Promise<Series | null>;
  loadDaily: (back: number, forward: number) => Promise<DailyOmzet | null>;
  loadTarget: (metric: string, grain: Grain, period: string) => Promise<TargetInfo | null>;
  saveTarget: (metric: string, grain: Grain, period: string, target: number) => Promise<boolean>;
}

export const useCijfersStore = create<CijfersState>((set, get) => ({
  grain: 'quarter',
  period: currentPeriod('quarter'),
  overview: null,
  overviewLoading: false,
  overviewError: null,
  seriesCache: {},
  seriesLoading: {},
  dailyCache: {},
  targetCache: {},

  setPeriod: (period, grain) => {
    set({ period, ...(grain ? { grain } : {}) });
    void get().loadOverview();
  },

  setGrain: (grain) => {
    // Grain-wissel springt naar de huidige periode van die grain.
    set({ grain, period: currentPeriod(grain) });
    void get().loadOverview();
  },

  loadOverview: async () => {
    const { period } = get();
    set({ overviewLoading: true, overviewError: null });
    try {
      const res = await apiFetch(`/api/kpi/overview?period=${encodeURIComponent(period)}`);
      if (!res.ok) throw new Error(`overview ${res.status}`);
      const data = await res.json();
      // Alleen toepassen als de periode intussen niet is doorgeklikt.
      if (get().period === period) {
        set({ overview: data.domains ?? {}, overviewLoading: false });
      }
    } catch (err) {
      console.error('[cijfers] overview', err);
      set({ overviewError: err instanceof Error ? err.message : String(err), overviewLoading: false });
    }
  },

  loadSeries: async (metric) => {
    const { grain, seriesCache, seriesLoading } = get();
    const cacheKey = `${metric}|${grain}`;
    if (seriesCache[cacheKey]) return seriesCache[cacheKey];
    if (seriesLoading[cacheKey]) return null;
    set({ seriesLoading: { ...seriesLoading, [cacheKey]: true } });
    try {
      // Volledige historie: Excel begint 2020-Q1; maandgrain heeft pas
      // vanaf 2023 live data, eerder is er simpelweg niets — geen probleem.
      const res = await apiFetch(
        `/api/kpi/series?metric=${encodeURIComponent(metric)}&grain=${grain}&from=2020-01&to=${new Date().toISOString().slice(0, 10)}`
      );
      if (!res.ok) throw new Error(`series ${metric} ${res.status}`);
      const series = (await res.json()) as Series;
      set((state) => ({
        seriesCache: { ...state.seriesCache, [cacheKey]: series },
        seriesLoading: { ...state.seriesLoading, [cacheKey]: false },
      }));
      return series;
    } catch (err) {
      console.error('[cijfers] series', metric, err);
      set((state) => ({ seriesLoading: { ...state.seriesLoading, [cacheKey]: false } }));
      return null;
    }
  },

  loadDaily: async (back, forward) => {
    const cacheKey = `${back}|${forward}`;
    const cached = get().dailyCache[cacheKey];
    if (cached) return cached;
    try {
      const res = await apiFetch(`/api/kpi/daily?back=${back}&forward=${forward}`);
      if (!res.ok) throw new Error(`daily ${res.status}`);
      const daily = (await res.json()) as DailyOmzet;
      set((state) => ({ dailyCache: { ...state.dailyCache, [cacheKey]: daily } }));
      return daily;
    } catch (err) {
      console.error('[cijfers] daily', err);
      return null;
    }
  },

  loadTarget: async (metric, grain, period) => {
    const cacheKey = `${metric}|${grain}|${period}`;
    const cached = get().targetCache[cacheKey];
    if (cached) return cached;
    try {
      const res = await apiFetch(
        `/api/kpi/targets?metric=${encodeURIComponent(metric)}&grain=${grain}&period=${encodeURIComponent(period)}`
      );
      if (!res.ok) throw new Error(`targets ${res.status}`);
      const info = (await res.json()) as TargetInfo;
      set((state) => ({ targetCache: { ...state.targetCache, [cacheKey]: info } }));
      return info;
    } catch (err) {
      console.error('[cijfers] target', err);
      return null;
    }
  },

  saveTarget: async (metric, grain, period, target) => {
    try {
      const res = await apiFetch('/api/kpi/targets', {
        method: 'PUT',
        body: JSON.stringify({ metric, grain, period, target }),
      });
      if (!res.ok) throw new Error(`targets PUT ${res.status}`);
      // Cache invalideren: carry-forward kan élke gecachete periode van deze
      // (metric, grain) raken, dus die keys allemaal weg.
      set((state) => ({
        targetCache: Object.fromEntries(
          Object.entries(state.targetCache).filter(([key]) => !key.startsWith(`${metric}|${grain}|`))
        ),
      }));
      return true;
    } catch (err) {
      console.error('[cijfers] saveTarget', err);
      return false;
    }
  },
}));
